export interface RequestConfig {
  url?: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  params?: Record<string, any>;
  data?: any;
  signal?: AbortSignal;
  responseType?: "json" | "blob" | "stream";
  credentials?: RequestCredentials;
  _retry?: boolean;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  config: RequestConfig;
}

export class ApiError extends Error {
  public readonly response?: {
    data: any;
    status: number;
    statusText: string;
    headers: Headers;
  };
  public readonly config?: RequestConfig;
  public readonly isApiError = true as const;

  constructor(
    message: string,
    config?: RequestConfig,
    response?: ApiError["response"]
  ) {
    super(message);
    this.name = "ApiError";
    this.config = config;
    this.response = response;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return (
    err instanceof ApiError ||
    (typeof err === "object" && err !== null && (err as any).isApiError === true)
  );
}

/**
 * Marca que um ApiError já foi tratado/exibido ao usuário (ex.: toast via
 * notifyError). Usado para suprimir o ruído de `onunhandledrejection` no Sentry
 * sem mascarar erros genuinamente não-tratados (esses NÃO recebem a marca e
 * continuam visíveis). Ver `utils/unhandled-rejection.ts` e `sentry.config.ts`.
 *
 * Symbol não-enumerável => não vaza em JSON.stringify / serialização.
 */
export const API_ERROR_HANDLED = Symbol("apiErrorHandled");

export function markApiErrorHandled(err: unknown): void {
  if (isApiError(err)) {
    (err as unknown as Record<symbol, boolean>)[API_ERROR_HANDLED] = true;
  }
}

export function isApiErrorHandled(err: unknown): boolean {
  return isApiError(err) && (err as unknown as Record<symbol, boolean>)[API_ERROR_HANDLED] === true;
}

export function isCancelError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}
